import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { apiGet, getErrorMessage } from '../../utils/api'
import { Button } from './Button'

interface AgentOption {
  id: string
  name: string
  email?: string
  role?: string
  open_chats?: number
}

interface AssignAgentModalProps {
  open: boolean
  count: number
  /** Title noun, e.g. "chat" or "ticket". */
  noun?: string
  /** Allow an "Unassign" choice (tickets support null assignment). */
  allowUnassign?: boolean
  busy?: boolean
  onCancel: () => void
  onConfirm: (agentId: string | null) => void
}

/**
 * Small modal that loads the agent roster and lets the user pick one to assign a
 * batch of selected rows to. Portaled to <body> so it always overlays maps,
 * sticky bars, and scroll containers.
 */
export const AssignAgentModal: React.FC<AssignAgentModalProps> = ({
  open,
  count,
  noun = 'item',
  allowUnassign = false,
  busy = false,
  onCancel,
  onConfirm,
}) => {
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string>('')

  useEffect(() => {
    if (!open) return
    setSelected('')
    setError(null)
    setLoading(true)
    apiGet<{ agents: AgentOption[] }>('/agents')
      .then((r) => setAgents(r.agents || []))
      .catch((err) => setError(getErrorMessage(err, 'Failed to load agents')))
      .finally(() => setLoading(false))
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-surface border border-border shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-base font-bold text-txt-primary">Assign {count} {noun}{count === 1 ? '' : 's'}</h3>
          <p className="text-xs text-txt-tertiary mt-0.5">Choose an agent to assign the selected {noun}s to.</p>
        </div>

        <div className="px-5 py-4 max-h-[50vh] overflow-y-auto">
          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-error-light text-error text-sm">{error}</div>
          )}
          {loading ? (
            <p className="text-sm text-txt-secondary py-6 text-center">Loading agents…</p>
          ) : (
            <div className="flex flex-col gap-1">
              {allowUnassign && (
                <button
                  onClick={() => setSelected('__unassign__')}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-left transition-colors ${
                    selected === '__unassign__'
                      ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-900/20'
                      : 'border-border hover:bg-surface-hover'
                  }`}
                >
                  <span className="text-sm font-medium text-txt-primary">Unassign</span>
                  <span className="text-xs text-txt-tertiary">Remove owner</span>
                </button>
              )}
              {agents.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelected(a.id)}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-left transition-colors ${
                    selected === a.id
                      ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-900/20'
                      : 'border-border hover:bg-surface-hover'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-txt-primary truncate">{a.name || a.email || a.id}</span>
                    {a.role && <span className="block text-xs text-txt-tertiary capitalize">{a.role}</span>}
                  </span>
                  {typeof a.open_chats === 'number' && (
                    <span className="text-xs text-txt-tertiary shrink-0 ml-2">{a.open_chats} open</span>
                  )}
                </button>
              ))}
              {!loading && agents.length === 0 && !error && (
                <p className="text-sm text-txt-secondary py-6 text-center">No agents found</p>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(selected === '__unassign__' ? null : selected)}
            disabled={busy || !selected}
            loading={busy}
          >
            {busy ? 'Assigning…' : 'Assign'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
